// The Ask sequence -- entry, proposal, preview, commit, batch-undo toast --
// per `#foundation` "Ask flow · component sequence" and `#s07`. Execution
// plan §8 WS-5 steps 4-7.
//
// Band-adaptive container (`useShellContext().band`): full-height sheet
// (mobile), 560px dialog (tablet), right panel (desktop). The design draws
// only the phone sheet fully and the tablet dialog partially (the file is
// truncated mid-artboard -- see the execution plan §0.1/§9 conflict #1); no
// `#s07` desktop artboard exists at all. The desktop branch below is built
// from the plan's prose only, per its explicit instruction (§8 WS-5 step 4):
// // design: prose-only, #s07 desktop artboard is missing
//
// This component is shaped to be a drop-in replacement for AppShell's
// `AskOverlayPlaceholder` (same `{ band, open, onOpenChange }` props) so a
// later integration step can swap it in without touching AppShell, which
// this workstream must not edit.
//
// No Ask endpoint exists at all (execution plan §9 conflict #9): every
// mutation from `useAskPreview`/`useAskCommit`/`useBatchUndo` rejects
// immediately against a real backend. This overlay therefore only ever
// reaches the `unavailable` honest state against live data; the full
// propose -> preview -> commit sequence runs in the dev harness and in
// colocated tests that mock those hook modules (WS-5 step 7).
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FocusScope } from '@radix-ui/react-focus-scope';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@moneyskyline/ui';
import { HonestInline } from '@/components/HonestInline';
import { showUndoToast } from '@/components/UndoToast';
import { useAskPreview, type AskProposal } from '@/api/hooks/useAskPreview';
import { useAskCommit } from '@/api/hooks/useAskCommit';
import { useBatchUndo } from '@/api/hooks/useBatchUndo';
import type { ShellViewportBand } from '@/app/navigation/viewportBand';
import { AskEntry } from './components/AskEntry';
import { AskProposalBody, selectedAskRows } from './components/AskProposalBody';
import { AskCommitForm } from './components/AskCommitForm';
import { AskSupersededBanner } from './components/AskSupersededBanner';

type AskPhase = 'entry' | 'loading' | 'preview' | 'superseded' | 'commit' | 'expired' | 'unavailable';

type AskOverlayState = {
  phase: AskPhase;
  setPhase: Dispatch<SetStateAction<AskPhase>>;
  sentence: string;
  setSentence: Dispatch<SetStateAction<string>>;
  proposal: AskProposal | null;
  setProposal: Dispatch<SetStateAction<AskProposal | null>>;
  keptIds: Set<string>;
  setKeptIds: Dispatch<SetStateAction<Set<string>>>;
};

const AskOverlayStateContext = createContext<AskOverlayState | null>(null);

function useAskOverlayState(): AskOverlayState {
  const state = useContext(AskOverlayStateContext);
  if (!state) throw new Error('useAskOverlayState must be used within AskOverlay');
  return state;
}

function defaultKeptIds(proposal: AskProposal): Set<string> {
  return new Set(proposal.matches.filter((row) => !row.struck).map((row) => row.id));
}

function isExpired(proposal: AskProposal): boolean {
  return new Date(proposal.expires_at).getTime() < Date.now();
}

export function AskOverlayBody({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) {
  const [, setSearchParams] = useSearchParams();
  // A `?draft=<id>` present on mount (e.g. a reload) has no fetch-by-id
  // endpoint -- only the propose mutation exists -- so a resumed draft is
  // honestly `draft-expired` rather than silently discarded or
  // re-fabricated (execution plan §8 WS-5 step 6). Computed once, lazily,
  // rather than via an effect that would call setState during render.
  const { phase, setPhase, sentence, setSentence, proposal, setProposal, keptIds, setKeptIds } = useAskOverlayState();

  const askPreview = useAskPreview();
  const askCommit = useAskCommit();
  const batchUndo = useBatchUndo();

  function runPreview(nextSentence: string) {
    setSentence(nextSentence);
    setPhase('loading');
    askPreview.mutate(
      { sentence: nextSentence },
      {
        onSuccess: (result) => {
          setProposal(result);
          setKeptIds(defaultKeptIds(result));
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('draft', result.draft_id);
            return next;
          });
          if (isExpired(result)) {
            setPhase('expired');
          } else if (result.new_matches_since_last_preview) {
            setPhase('superseded');
          } else {
            setPhase('preview');
          }
        },
        onError: () => setPhase('unavailable'),
      },
    );
  }

  function toggleKept(id: string) {
    setKeptIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function goToCommit() {
    if (!proposal) return;
    if (isExpired(proposal)) {
      setPhase('expired');
      return;
    }
    setPhase('commit');
  }

  function commit(input: { eventName: string; tagBusiness: boolean; saveCondition: boolean }) {
    if (!proposal) return;
    if (isExpired(proposal)) {
      setPhase('expired');
      return;
    }
    askCommit.mutate(
      {
        draft_id: proposal.draft_id,
        accept_ids: selectedAskRows(proposal, keptIds).map((row) => row.id), // KEPT ids, never an exclusion list
        event_name: input.eventName,
        tag_business: input.tagBusiness,
        save_condition: input.saveCondition,
      },
      {
        onSuccess: (result) => {
          showUndoToast({
            message: `Saved "${input.eventName}" · ${result.count} items tagged`,
            batchId: result.batch_id,
            onUndo: (batchId) => batchUndo.undo.mutate(batchId),
          });
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('draft');
            return next;
          });
          onOpenChange(false);
        },
        onError: () => setPhase('unavailable'),
      },
    );
  }

  if (phase === 'entry') {
    return <AskEntry initialSentence={sentence} onSubmit={runPreview} />;
  }

  if (phase === 'loading') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite">
        <span
          aria-hidden="true"
          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-border border-t-link"
        />
        Reading recent items…
      </div>
    );
  }

  if (phase === 'unavailable') {
    return (
      <HonestInline
        kind="not-built-yet"
        action={{ label: 'Back', onAction: () => setPhase('entry') }}
      />
    );
  }

  if (phase === 'expired') {
    function restartRequest() {
      setProposal(null);
      setKeptIds(new Set());
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('draft');
        return next;
      });
      setPhase('entry');
    }

    return (
      <div className="flex flex-col items-center gap-4 p-6 text-center" data-testid="route-state-draft-expired">
        <h2 className="text-2xl font-bold">This preview expired</h2>
        <p className="max-w-md text-muted-foreground">Re-run the preview with the same sentence.</p>
        <button type="button" onClick={restartRequest} className="min-h-11 rounded-md bg-primary px-4 py-2 text-primary-foreground">
          Start a new request
        </button>
      </div>
    );
  }

  if (!proposal) return null;

  if (phase === 'superseded') {
    return (
      <div className="flex flex-col gap-3">
        <span className="sr-only" data-testid="ask-retained-sentence">{sentence}</span>
        <AskSupersededBanner
          newMatchCount={proposal.new_matches_since_last_preview ?? 0}
          onRepreview={() => runPreview(sentence)}
        />
        <div className="opacity-50" aria-disabled="true">
          <AskProposalBody proposal={proposal} keptIds={keptIds} onToggle={() => undefined} />
        </div>
      </div>
    );
  }

  if (phase === 'commit') {
    return (
      <div className="flex flex-col gap-3">
        <AskCommitForm
          eventName={proposal.event_name}
          keptCount={keptIds.size}
          pending={askCommit.isPending}
          onBack={() => setPhase('preview')}
          onSave={commit}
        />
      </div>
    );
  }

  // phase === 'preview'
  return (
    <div className="flex flex-col gap-3">
      <span className="sr-only" data-testid="ask-retained-sentence">{sentence}</span>
      <AskProposalBody proposal={proposal} keptIds={keptIds} onToggle={toggleKept} />
      <button
        type="button"
        onClick={goToCommit}
        disabled={keptIds.size === 0}
        className="min-h-11 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
      >
        Continue · {keptIds.size} item{keptIds.size === 1 ? '' : 's'}
      </button>
    </div>
  );
}

export function AskOverlay({
  band,
  open,
  onOpenChange,
}: {
  band: ShellViewportBand;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<AskPhase>(() => (searchParams.get('draft') ? 'expired' : 'entry'));
  const [sentence, setSentence] = useState('');
  const [proposal, setProposal] = useState<AskProposal | null>(null);
  const [keptIds, setKeptIds] = useState<Set<string>>(new Set());
  const openerRef = useRef<HTMLElement | null>(null);

  const state: AskOverlayState = {
    phase,
    setPhase,
    sentence,
    setSentence,
    proposal,
    setProposal,
    keptIds,
    setKeptIds,
  };

  useLayoutEffect(() => {
    function rememberOverlayOpener(event: FocusEvent) {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target !== document.body &&
        target !== document.documentElement &&
        !target.closest('[role="dialog"]')
      ) {
        openerRef.current = target;
      }
    }

    document.addEventListener('focusin', rememberOverlayOpener);
    return () => document.removeEventListener('focusin', rememberOverlayOpener);
  }, []);

  const focusOpener = useCallback(() => {
    const opener = openerRef.current?.isConnected
      ? openerRef.current
      : document.querySelector<HTMLElement>('button[aria-haspopup="dialog"]');
    opener?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function closeFromDocument(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onOpenChange(false);
      focusOpener();
    }
    document.addEventListener('keydown', closeFromDocument, true);
    return () => document.removeEventListener('keydown', closeFromDocument, true);
  }, [focusOpener, onOpenChange, open]);

  function restoreOpenerFocus(event: Event) {
    event.preventDefault();
    focusOpener();
  }

  function closeDesktopOverlay(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    onOpenChange(false);
    focusOpener();
  }

  if (band === 'mobile') {
    return (
      <AskOverlayStateContext.Provider value={state}>
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            side="full"
            aria-describedby="ask-overlay-description"
            onKeyDown={closeDesktopOverlay}
            onCloseAutoFocus={restoreOpenerFocus}
          >
            <SheetHeader>
              <SheetTitle>Ask MoneySkyline</SheetTitle>
              <SheetDescription id="ask-overlay-description">
                Ask a question about your own ledger in plain English.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-3">
              <AskOverlayBody onOpenChange={onOpenChange} />
            </div>
          </SheetContent>
        </Sheet>
      </AskOverlayStateContext.Provider>
    );
  }

  if (band === 'tablet') {
    return (
      <AskOverlayStateContext.Provider value={state}>
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent
            className="max-w-[560px]"
            aria-describedby="ask-overlay-description"
            onKeyDown={closeDesktopOverlay}
            onCloseAutoFocus={restoreOpenerFocus}
          >
            <DialogHeader>
              <DialogTitle>Ask MoneySkyline</DialogTitle>
              <DialogDescription id="ask-overlay-description">
                Ask a question about your own ledger in plain English.
              </DialogDescription>
            </DialogHeader>
            <AskOverlayBody onOpenChange={onOpenChange} />
          </DialogContent>
        </Dialog>
      </AskOverlayStateContext.Provider>
    );
  }

  // design: prose-only, #s07 desktop artboard is missing. The plan (§8 WS-5
  // step 4) specifies "the same content as a right-side panel over the feed,
  // so matches can be compared against the list" with no measured width or
  // chrome -- this is a minimal, honestly-labelled build from that prose,
  // not an invented artboard.
  if (!open) return null;
  return (
    <AskOverlayStateContext.Provider value={state}>
      <FocusScope
        asChild
        loop
        trapped
        onUnmountAutoFocus={restoreOpenerFocus}
      >
        <div
          role="dialog"
          aria-modal="false"
          aria-describedby="ask-overlay-description"
          onKeyDown={closeDesktopOverlay}
          data-testid="ask-desktop-panel"
          aria-label="Ask MoneySkyline"
          className="fixed inset-y-0 right-0 z-40 flex w-[420px] flex-col gap-3 overflow-y-auto border-l border-border bg-background p-4 shadow-overlay"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Ask MoneySkyline</h2>
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                focusOpener();
              }}
              className="min-h-11 px-2 text-sm text-muted-foreground"
            >
              Close
            </button>
          </div>
          <p id="ask-overlay-description" className="sr-only">
            Ask a question about your own ledger in plain English.
          </p>
          <AskOverlayBody onOpenChange={onOpenChange} />
        </div>
      </FocusScope>
    </AskOverlayStateContext.Provider>
  );
}

export default AskOverlay;
